import { createFileRoute, Link } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { useConsent } from "@/lib/consent/ConsentProvider";

import { pageSchemas } from "@/lib/seo/page-schema";
export const Route = createFileRoute("/bg/cookie-policy")({
  head: () => ({
    meta: [
      { title: "Политика за бисквитки | Perceptica" },
      { name: "description", content: "Как Perceptica използва бисквитки в платформата за медиен мониторинг - какво задаваме, защо и как да го контролирате във вашия браузър." },
      { property: "og:title", content: "Политика за бисквитки | Perceptica" },
      { property: "og:description", content: "Как Perceptica използва бисквитки в платформата за медиен мониторинг - какво задаваме, защо и как да го контролирате във вашия браузър." },

      ...ogImageMeta("bg", "/cookie-policy", "Политика за бисквитки на Perceptica"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Политика за бисквитки | Perceptica" },
      { name: "twitter:description", content: "Как Perceptica използва бисквитки в платформата за медиен мониторинг - какво задаваме, защо и как да го контролирате във вашия браузър." },
      { property: "og:url", content: "https://perceptica.com/bg/cookie-policy" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "bg", path: "/cookie-policy" }),
  }),
  component: CookiePolicyBGPage,
});

function CookiePolicyBGPage() {
  const { reopen, acceptAll, rejectAll, consent } = useConsent();
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-20 lg:px-10 lg:py-28">
        <h1 className="font-display text-[clamp(2rem,3.4vw,2.8rem)] font-medium text-foreground">Политика за бисквитки</h1>
        <p className="mt-4 text-[14px] text-foreground/70">Последна актуализация: май 2026</p>

        <div className="prose prose-neutral mt-8 max-w-none text-[14.5px] leading-relaxed text-foreground/85">
          <h2 className="font-display mt-10 text-xl">1. Въведение</h2>
          <p>
            Бисквитките и сходните технологии са малки файлове, съхранявани на вашето устройство, когато
            посещавате уебсайт. Използваме ги, за да осигурим работата на сайта, да запомним вашите
            предпочитания и, с ваше съгласие, да разберем как се използва сайтът, за да го подобряваме.
          </p>

          <h2 className="font-display mt-8 text-xl">2. Кой е отговорен</h2>
          <p>
            Perceptica е администратор на личните данни, обработвани чрез бисквитки на този сайт. За
            въпроси относно тази политика се свържете с нас на{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
          </p>

          <h2 className="font-display mt-8 text-xl">3. Категории бисквитки, които използваме</h2>
          <ul className="mt-3 space-y-2">
            <li><strong>Строго необходими</strong> - нужни за функционирането на сайта (сигурност, сесия, съхранение на вашия избор за съгласие). Винаги активни; не могат да бъдат изключени.</li>
            <li><strong>Аналитични</strong> - обобщена, анонимна статистика за използването, която ни помага да подобряваме производителността и съдържанието. Задават се само след съгласие.</li>
            <li><strong>Маркетингови</strong> - използват се за измерване на кампании и адаптиране на съобщенията. Задават се само след съгласие.</li>
          </ul>

          <h2 className="font-display mt-8 text-xl">4. Бисквитки и съхранение, които задаваме</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-[13.5px]">
              <thead>
                <tr className="border-b border-border/70 text-left text-foreground">
                  <th className="py-2 pr-3 font-medium">Име</th>
                  <th className="py-2 pr-3 font-medium">Цел</th>
                  <th className="py-2 pr-3 font-medium">Категория</th>
                  <th className="py-2 font-medium">Срок</th>
                </tr>
              </thead>
              <tbody className="text-foreground/80">
                <tr className="border-b border-border/40">
                  <td className="py-2 pr-3 font-mono text-[12.5px]">perceptica.consent.v1</td>
                  <td className="py-2 pr-3">Съхранява избора ви за съгласие за бисквитки (localStorage).</td>
                  <td className="py-2 pr-3">Строго необходими</td>
                  <td className="py-2">До 12 месеца</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="py-2 pr-3 font-mono text-[12.5px]">perceptica_locale</td>
                  <td className="py-2 pr-3">Запомня избрания език (EN / BG).</td>
                  <td className="py-2 pr-3">Строго необходими</td>
                  <td className="py-2">12 месеца</td>
                </tr>
                <tr className="border-b border-border/40">
                  <td className="py-2 pr-3">Аналитични бисквитки</td>
                  <td className="py-2 pr-3">Обобщени метрики за трафик и производителност.</td>
                  <td className="py-2 pr-3">Аналитични</td>
                  <td className="py-2">До 13 месеца</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Маркетингови бисквитки</td>
                  <td className="py-2 pr-3">Измерване и атрибуция на кампании.</td>
                  <td className="py-2 pr-3">Маркетингови</td>
                  <td className="py-2">До 13 месеца</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="font-display mt-8 text-xl">5. Трети страни и подизпълнители</h2>
          <p>
            Аналитичните и маркетинговите бисквитки могат да бъдат задавани от доверени подизпълнители
            (например доставчици на хостинг и анализ), обвързани със споразумения за обработване на
            данни. Не продаваме лични данни.
          </p>

          <h2 className="font-display mt-8 text-xl">6. Вашият избор</h2>
          <p>
            При първото ви посещение банер ви пита за съгласие за незадължителни бисквитки. Можете да
            промените избора си по всяко време с контролите по-долу или като отворите отново банера за
            съгласие. Можете също да блокирате или изтривате бисквитки чрез настройките на браузъра си,
            макар че това може да повлияе на работата на части от сайта.
          </p>

          <h2 className="font-display mt-8 text-xl">7. Do Not Track и Global Privacy Control</h2>
          <p>
            Зачитаме явни сигнали за поверителност като Global Privacy Control (GPC) там, където е
            технически възможно, и ги третираме като заявка за отказ от незадължителни бисквитки.
          </p>

          <h2 className="font-display mt-8 text-xl">8. Срок на съхранение</h2>
          <p>
            Записът за съгласие се съхранява до 12 месеца, след което питаме отново. Аналитичните и
            маркетинговите данни, получени от бисквитки, се съхраняват до 13 месеца, освен ако не се
            прилага по-кратък срок.
          </p>

          <h2 className="font-display mt-8 text-xl">9. Промени в тази политика</h2>
          <p>
            Може да актуализираме тази политика периодично. Съществените промени ще бъдат отразени с
            нова дата "Последна актуализация" в горната част на страницата.
          </p>

          <h2 className="font-display mt-8 text-xl">10. Контакт</h2>
          <p>
            За въпроси относно бисквитки или вашия избор за поверителност пишете на{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
            Вижте нашата <Link to="/bg/privacy-policy" className="underline underline-offset-2">Политика за поверителност</Link> за пълната
            информация относно обработването на лични данни.
          </p>

          <h2 className="font-display mt-10 text-xl">Управление на съгласието</h2>
          <p className="text-[13.5px] text-foreground/70">
            Текущо състояние: <strong>{consent ? `решено на ${new Date(consent.decidedAt).toLocaleDateString()}` : "не е зададено"}</strong>
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={acceptAll}>Приемам всички</Button>
          <Button variant="outline" onClick={rejectAll}>Отхвърлям незадължителните</Button>
          <Button variant="ghost" onClick={reopen}>Покажи отново банера</Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
